import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEditor } from "./useEditorState";

export const FIELD_TYPES = { text: "Testo breve", textarea: "Testo lungo", email: "Email", tel: "Telefono", number: "Numero", date: "Data",
  select: "Scelta singola (menu)", radio: "Scelta singola (pulsanti)", multi: "Scelta multipla", checkbox: "Casella sì/no" };
const WITH_OPTS = ["select", "radio", "multi"];
const slug = (s, used) => { let k = (s.normalize("NFKD").replace(/[^\w\s]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "campo").slice(0, 36); if (!/^[a-z]/.test(k)) k = `f_${k}`; let o = k, n = 2; while (used.has(o)) o = `${k}_${n++}`; return o; };

function FieldRow({ f, i, n, upd, move, del }) {
  return (
    <li className="space-y-1.5 rounded-md border border-slate-200 p-2" data-testid={`fb-field-${i}`}>
      <div className="flex items-center gap-1">
        <Input value={f.label} onChange={(e) => upd({ label: e.target.value })} placeholder="Etichetta" className="h-7 text-xs" data-testid={`fb-label-${i}`} />
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!i} onClick={() => move(-1)} aria-label="Su" data-testid={`fb-up-${i}`}><ArrowUp size={13} /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === n - 1} onClick={() => move(1)} aria-label="Giù" data-testid={`fb-down-${i}`}><ArrowDown size={13} /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={del} aria-label="Elimina campo" data-testid={`fb-del-${i}`}><Trash2 size={13} /></Button>
      </div>
      <div className="flex items-center gap-2">
        <Select value={f.type} onValueChange={(v) => upd({ type: v })}><SelectTrigger className="h-7 flex-1 text-xs" data-testid={`fb-type-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(FIELD_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <label className="flex items-center gap-1 text-[11px] text-slate-600"><Switch checked={!!f.required} onCheckedChange={(v) => upd({ required: v })} data-testid={`fb-required-${i}`} />Obbligatorio</label>
      </div>
      {WITH_OPTS.includes(f.type) ? <Input value={f.optText ?? (f.options || []).join(", ")} onChange={(e) => upd({ optText: e.target.value })} placeholder="Opzioni separate da virgola" className="h-7 text-xs" data-testid={`fb-options-${i}`} />
        : f.type !== "checkbox" && <Input value={f.placeholder || ""} onChange={(e) => upd({ placeholder: e.target.value })} placeholder="Testo d'esempio (facoltativo)" className="h-7 text-xs" data-testid={`fb-placeholder-${i}`} />}
    </li>
  );
}

// Fields of the REAL form bound to this section (site_forms of the client): same definition used by the site, the server validation and the CRM.
export default function FormBuilder({ s }) {
  const ed = useEditor();
  const [ctx, setCtx] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = s.props?.formKey;
  useEffect(() => {
    (async () => {
      try {
        const { data } = await ed.api.get(`/projects/${ed.id}/site`);
        const tid = data.site?.client_id || data.site?.tenant_id;
        const forms = (await ed.api.get(`/t/${tid}/forms`)).data;
        const form = forms.find((x) => x.form_key === key);
        setCtx({ tid }); setF(form ? { ...form, fields: form.fields.map((x) => ({ ...x })) } : false);
      } catch { setF(false); }
    })();
  }, [ed.api, ed.id, key]);
  if (f === null) return <p className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" />Caricamento modulo…</p>;
  if (!f) return <p className="text-xs text-amber-700" data-testid="fb-missing">Modulo reale non trovato per questa sezione.</p>;
  const fields = f.fields;
  const setFields = (x) => setF({ ...f, fields: x });
  const save = async () => {
    const used = new Set();
    const out = fields.filter((x) => x.label.trim()).map((x) => {
      const k = x.key && !used.has(x.key) ? x.key : slug(x.label, used); used.add(k);
      const { optText, ...rest } = x;
      return { ...rest, key: k, label: x.label.trim(), options: WITH_OPTS.includes(x.type) ? (optText ?? (x.options || []).join(",")).split(",").map((o) => o.trim()).filter(Boolean) : [] };
    });
    setBusy(true);
    try {
      await ed.save();
      const body = { site_id: f.site_id, name: f.name, purpose: f.purpose, fields: out, consent_required: f.consent_required, consent_text: f.consent_text, success_message: f.success_message, notify: f.notify, enabled: f.enabled };
      const { data } = await ed.api.put(`/t/${ctx.tid}/forms/${f.id}`, body);
      setF({ ...data, fields: data.fields.map((x) => ({ ...x })) }); await ed.load(); toast.success("Modulo salvato: il sito usa i nuovi campi");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="form-builder">
      <p className="text-[11px] text-slate-500">Modulo reale «{f.name}»: le risposte arrivano nel CRM del cliente (Richieste), con consenso privacy e protezione anti-spam.</p>
      <ul className="space-y-1.5">{fields.map((x, i) => <FieldRow key={i} f={x} i={i} n={fields.length}
        upd={(p) => setFields(fields.map((y, j) => (j === i ? { ...y, ...p } : y)))}
        move={(d) => { const c = [...fields]; [c[i], c[i + d]] = [c[i + d], c[i]]; setFields(c); }}
        del={() => setFields(fields.filter((_, j) => j !== i))} />)}</ul>
      <Button size="sm" variant="outline" className="h-7 w-full text-xs" disabled={fields.length >= 30} onClick={() => setFields([...fields, { key: "", label: "Nuovo campo", type: "text", required: false }])} data-testid="fb-add"><Plus size={13} className="mr-1" />Aggiungi campo</Button>
      <label className="flex items-center justify-between text-xs text-slate-700">Consenso privacy obbligatorio<Switch checked={!!f.consent_required} onCheckedChange={(v) => setF({ ...f, consent_required: v })} data-testid="fb-consent" /></label>
      {f.consent_required && <Textarea value={f.consent_text} onChange={(e) => setF({ ...f, consent_text: e.target.value })} rows={2} className="text-xs" data-testid="fb-consent-text" />}
      <label className="block text-xs text-slate-700">Messaggio di conferma<Textarea value={f.success_message} onChange={(e) => setF({ ...f, success_message: e.target.value })} rows={2} className="mt-1 text-xs" data-testid="fb-success" /></label>
      <Button size="sm" className="w-full" disabled={busy || !fields.some((x) => x.label.trim())} onClick={save} data-testid="fb-save">{busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}Salva modulo</Button>
    </div>
  );
}
